import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiContext } from '@api/api.tsx';
import { CompanyLocationsListPreviewOutDTO, RampOpeningHoursByDateRangeOutDTO, ReservationCalendarGroupedOutDTO } from '@api/ringil-api.v2';
import { CalendarViewType, getDateRange, getDaysArray } from './calendarView';
import { transformClosingHoursData, transformReservationData } from './calendarEventMapping';
import { buildRampOrderLookup, sortByRampOrder } from './rampOrder';
import { pruneCalendarSelection } from './calendarSelection';

interface Params {
  locationIds: string[];
  rampIds: string[];
  currentDate: string;
  currentView: CalendarViewType;
  /** False while the filter is still resolving — skips the pointless first fetch with an empty filter. */
  enabled?: boolean;
}

/**
 * Single owner of every calendar fetch.
 *
 * Previously the "reservations for each day of the range" block lived in three places (the loader,
 * a `currentDate` effect and the drag-stop handler), which meant a date change fetched twice and
 * nothing ordered the responses. Here it runs once per (filter × date × view) change, and a
 * sequence guard drops batches that resolve after a newer one — clicking prev/next quickly can no
 * longer paint yesterday's reservations over today's grid.
 *
 * The location list is independent of the filter and the shown day, so it is loaded once on mount
 * instead of on every ramp toggle.
 */
export const useCalendarData = ({ locationIds, rampIds, currentDate, currentView, enabled = true }: Params) => {
  const { getRampsOpeningHoursForDateRange, getReservationsForDayAndMultipleLocations, getDashboardLocations } = useContext(ApiContext);

  const [ramps, setRamps] = useState<RampOpeningHoursByDateRangeOutDTO[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [openingHours, setOpeningHours] = useState<Array<any> | null>(null);
  const [locations, setLocations] = useState<Array<CompanyLocationsListPreviewOutDTO>>([]);
  const [locationsWithRamps, setLocationsWithRamps] = useState<Array<CompanyLocationsListPreviewOutDTO>>([]);
  // Nothing may be fetched before the location list answers: the persisted filter can point at a
  // location that was deleted or superseded in the meantime, and only this list says which ids
  // still exist (see calendarSelection.ts).
  const [locationsLoaded, setLocationsLoaded] = useState(false);

  // Only the newest request may write into state; anything older is a stale response.
  const requestSeq = useRef(0);

  // Arrays are recreated on every render — compare them by content so the effect below doesn't loop.
  const locationIdsKey = (locationIds ?? []).join(',');
  const rampIdsKey = (rampIds ?? []).join(',');

  const load = useCallback(() => {
    if (!enabled || !locationsLoaded) return;
    const seq = ++requestSeq.current;
    const { locationIds: safeLocationIds, rampIds: safeRampIds } = pruneCalendarSelection({ locationIds, rampIds }, locations);
    const { dateFrom, dateTo } = getDateRange(currentDate, currentView);

    getRampsOpeningHoursForDateRange(
      {
        rampIds: safeRampIds,
        dateFrom,
        dateTo,
        timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        showTemporaryClosed: false,
      },
      d => {
        if (seq !== requestSeq.current) return;
        setRamps(d);
        setOpeningHours(transformClosingHoursData(d));
      },
    );

    Promise.all(
      getDaysArray(currentDate, currentView).map(
        day =>
          new Promise<ReservationCalendarGroupedOutDTO[]>(resolve => {
            getReservationsForDayAndMultipleLocations(
              {
                locationIds: safeLocationIds,
                dayToShow: day,
                rampIds: safeRampIds,
              },
              res => resolve(res),
            );
          }),
      ),
    ).then(results => {
      if (seq !== requestSeq.current) return;
      setEvents(transformReservationData(results.flat()));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationIdsKey, rampIdsKey, currentDate, currentView, enabled, locationsLoaded, locations]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getDashboardLocations(null, true, data => {
      const sortedLocations = [...data].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      setLocations(sortedLocations);
      setLocationsWithRamps(sortedLocations.filter(location => location?.ramps.length > 0));
      setLocationsLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The opening-hours response has no ramp ordering, so the BE order from the location list is applied
  // here — it also drives the detail-view ramp paging, which walks this array.
  const rampOrderLookup = useMemo(() => buildRampOrderLookup(locationsWithRamps), [locationsWithRamps]);
  const orderedRamps = useMemo(() => sortByRampOrder(ramps, rampOrderLookup), [ramps, rampOrderLookup]);

  return { ramps: orderedRamps, rampOrderLookup, events, openingHours, locations, locationsWithRamps, locationsLoaded, reload: load };
};